import {
    GET_DOCUMENT_FOLDERS,
    DELETE_DOCUMENT,
    GET_DOCUMENT_DATA,
    GET_RECENT_DOCUMENTS,
    SET_RECENT_DOCUMENT_COUNT,
    SET_RECENT_DOCUMENTS,
    SET_DOCUMENT_ACTIVITY,
    UPDATE_DOCUMENT_FILE,
    SET_SHARED_DOCUMENT_FILES,
    GET_SHARED_DOCUMENT_FILES,
    SET_FILE_LIST_MAX_PAGE,
    SET_ACTIVE_DOCUMENT,
    RESET_DOCUMENT_STATE,
    SET_FOLDER_FILES_TOTAL,
    SET_DOCUMENT_LOCK,
    CLEAR_DOCUMENT_LOCK,
    LOCK_DOCUMENT,
    UNLOCK_DOCUMENT,
    UPDATE_SYNCFUSION_STATE,
    GET_SYNCFUSION_ASSETS
} from './actions';

import DocumentService from '@services/document.service';
import KindergartenService from '@services/kindergarten.service';
import {START_CONTENT_LOADER, STOP_CONTENT_LOADER} from '@store/loader/actions';
import { loadScript } from '@/utils/assetLoader';

const getDefaultState = () => {
    return {
        last_activity: null,
        recent_documents: [],
        shared_document_files: [],
        unseen_count: 0,
        fileListMaxPage: 0,
        permissions: {
            manage: 'manage',
            view: 'view',
            edit: 'edit'
        },
        folderTotalFiles: 0,
        activeDocument: null,
        lock: {
            locked: false,
            locked_by: null,
            locked_at: null,
            lock_owner: null
        },
        syncfusionReady: false,
        syncfusionLoading: false
    };
};

const state = getDefaultState();

const mutations = {
    //don't use until recent stuff is moved away from this store
    [RESET_DOCUMENT_STATE](state) {
        Object.assign(state, getDefaultState());
    },
    [SET_DOCUMENT_ACTIVITY](state, date) {
        state.last_activity = date;
    },
    [SET_RECENT_DOCUMENTS](state, data) {
        state.recent_documents = data;
    },
    [SET_RECENT_DOCUMENT_COUNT](state, count) {
        if(count > 5) {
            count = '5+';
        }
        state.unseen_count = count;
    },
    [SET_SHARED_DOCUMENT_FILES](state, data) {
        //paginated data
        data.forEach(doc => {
            state.shared_document_files.push(doc);
        });
    },
    [SET_FILE_LIST_MAX_PAGE](state, pages) {
        state.fileListMaxPage = pages;
    },
    [SET_FOLDER_FILES_TOTAL](state, total) {
        state.folderTotalFiles = total;
    },
    [SET_ACTIVE_DOCUMENT](state, doc) {
        state.activeDocument = doc;
    },
    [SET_DOCUMENT_LOCK](state, payload) {
        state.lock.locked = true;
        state.lock.locked_by = payload.locked_by;
        state.lock.locked_at = payload.locked_at;
        state.lock.lock_owner = payload.lock_owner;
    },
    [CLEAR_DOCUMENT_LOCK](state) {
        state.lock.locked = false;
        state.lock.locked_by = null;
        state.lock.locked_at = null;
        state.lock.lock_owner = null;
    },
    [UPDATE_SYNCFUSION_STATE](state, status) {
        state.syncfusionLoading = status === 'loading';
        state.syncfusionReady =
            !!window.ej?.spreadsheet?.Spreadsheet &&
            !!window.ej?.documenteditor?.DocumentEditor;
    }
};

const actions = {
    [GET_DOCUMENT_FOLDERS]({ commit, getters}, queryObj) {
        if(queryObj.page === 1) {
            commit('RESET_SUB_FOLDERS');
            commit('SET_FOLDER_MAX_PAGE', 0);
            commit('SET_FOLDER_TOTAL', 0);
        }
        return DocumentService.getIndexData(getters.activeKindergartenId, queryObj).then(res => {
            commit('ADD_TO_SUB_FOLDERS', res.folders.data);
            commit('SET_FOLDER_SETTINGS', res.settings);
            commit('SET_FOLDER_MAX_PAGE', res.folders.last_page);
            commit('SET_FOLDER_TOTAL', res.folders.total);
        });
    },
    [DELETE_DOCUMENT]({ commit, getters }, fileName) {
        return DocumentService.deleteDocument(getters.activeKindergartenId, fileName).then(() => {
            commit('REMOVE_FOLDER_FILE', fileName);
        });
    },
    [GET_DOCUMENT_DATA]({ getters }, fileId) {
        return DocumentService.getDocumentData(getters.activeKindergartenId, fileId);
    },
    [GET_RECENT_DOCUMENTS]({ commit }, kindergarten_id) {
        return KindergartenService.getRecentFiles(kindergarten_id).then(res => {
            commit(SET_RECENT_DOCUMENT_COUNT, res.count);
            commit(SET_RECENT_DOCUMENTS, res.documents);
        });
    },
    [SET_DOCUMENT_ACTIVITY]({commit, getters}) {
        return DocumentService.updateActivity(getters.activeKindergartenId).then(res => {
            commit(SET_DOCUMENT_ACTIVITY, res.date);
            commit(SET_RECENT_DOCUMENT_COUNT, 0);
        });
    },
    [UPDATE_DOCUMENT_FILE]({commit, getters}, form) {
        return DocumentService.updateDocumentFile(getters.activeKindergartenId, form.filename, form).then(res => {
            commit('UPDATE_FOLDER_FILE', res);
        });
    },
    [GET_SHARED_DOCUMENT_FILES]({ commit, getters, state }, queryObj) {
        //reset data on index reentry or it will loop old data as new data to existing data
        if(queryObj.page === 1) {
            state.shared_document_files = [];
            commit(SET_FILE_LIST_MAX_PAGE, 0);
        }
        return DocumentService.getSharedDocuments(getters.activeKindergartenId, queryObj).then(res => {
            if (queryObj.term === '') {
                commit(SET_FOLDER_FILES_TOTAL, res.total);
            }
            commit(SET_SHARED_DOCUMENT_FILES, res.data);
            commit(SET_FILE_LIST_MAX_PAGE, res.last_page);
            commit(SET_FOLDER_FILES_TOTAL, res.total);
        });
    },
    [SET_ACTIVE_DOCUMENT]({ commit }, queryObj) {
        return commit(SET_ACTIVE_DOCUMENT, queryObj);
    },
    [LOCK_DOCUMENT]({ commit, getters }, fileId) {
        return DocumentService.lockDocument(getters.activeKindergartenId, fileId).then(res => {
            if (res.locked) {
                commit(SET_DOCUMENT_LOCK, res.locked_data);
            } else {
                commit(CLEAR_DOCUMENT_LOCK);
            }
            return res;
        });
    },
    [UNLOCK_DOCUMENT]({ commit, getters }, { fileId, lockedAt }) {
        return DocumentService.unlockDocument(getters.activeKindergartenId, fileId, lockedAt).then(() => {
            commit(CLEAR_DOCUMENT_LOCK);
        });
    },
    async [GET_SYNCFUSION_ASSETS]({ commit, state }) {
        commit(UPDATE_SYNCFUSION_STATE);
        if (state.syncfusionReady || state.syncfusionLoading) {
            return;
        }
        try {
            // New license key is needed when doing major version change
            const syncfusionVersion = '34.2.8';
            commit(START_CONTENT_LOADER);
            commit(UPDATE_SYNCFUSION_STATE, 'loading');

            await loadScript(`https://cdn.syncfusion.com/ej2/${syncfusionVersion}/dist/ej2.min.js`);

            if (window.ej?.base) {
                const documentEditorLicense = process.env.VUE_APP_SYNCFUSION_WORD_KEY;
                const spreadsheetLicense = process.env.VUE_APP_SYNCFUSION_SPREADSHEET_KEY;
                window.ej.base.registerLicense(`${documentEditorLicense};${spreadsheetLicense}`);
                return window.ej.base;
            }
        } finally {
            commit(STOP_CONTENT_LOADER);
            commit(UPDATE_SYNCFUSION_STATE);
        }
    }
};

const getters = {
    recentDocuments: state => {
        return state.recent_documents.slice(0, 3) || [];
    },
    unseenDocumentCount: state => state.unseen_count,
    sharedDocumentFiles: state => state.shared_document_files,
    fileListMaxPage: state => state.fileListMaxPage,
    activeDocument: state => state.activeDocument,
    folderTotalFiles: state => state.folderTotalFiles,
    documentLock: state => state.lock,
    isSyncfusionReady: state => state.syncfusionReady
};

export default {
    state,
    mutations,
    actions,
    getters
};
